


import { CommonModule, DatePipe, Location } from "@angular/common";
import { Component, HostListener } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { RouterModule, ActivatedRoute, Router, NavigationStart } from "@angular/router";
import { Subscription } from 'rxjs';
import { AttachmentDeleteRequest, FileInfo } from '@models/attachment.model'
import { OrderFilesProfileQueryResponseInfo } from "@models/order-files.model";
import { OrderRecordDetailsResponseInfo, OrderFilesResponseInfo, OrderRecordCreateRequest, FileIdsInfo, } from "@models/order-records.model";

import { DecomUploadService } from '@services/decom-upload/decom-upload-service'
import { OrderService } from "@services/order/order-service";
import { DateTimeFormat } from "@utils/date-time-format";
import { MenuService } from "@utils/menu-active";
/**
 * XRayDetailNew component.
 * Handles the display and logic for the X-ray detail page,
 * including form handling and date formatting.
 */
@Component({
  selector: "app-x-ray-detail-new",
  standalone: true,
  providers: [DatePipe],

  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    DateTimeFormat
  ],
  templateUrl: "./x-ray-detail-new.html",
  styleUrl: "./x-ray-detail-new.scss",
})
export class XRayDetailNew {


  fileList: OrderFilesProfileQueryResponseInfo[] = []
  hospitalName !: string;
  selectedProfileId !: string;
  selectedUserName !: string;
  navigationSubscription: Subscription;
  goingToBackPage: boolean = false; // ✅ New flag
  isOrderCreated: boolean = false;
  order_id !: string
  orderDetails: OrderRecordDetailsResponseInfo[] = []
  orderFiles: OrderFilesResponseInfo[] = []
  currentDate = new Date().toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });;
  loading: boolean = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customerData: any;

  constructor(private route: ActivatedRoute, private orderService: OrderService, private location: Location, private router: Router, private decomUploadService: DecomUploadService, private menuService: MenuService) {
    // Listen for Angular route changes
    this.navigationSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.handlePageLeave();
      }
    });
  }

  // ✅ Browser Close / Refresh Warning
  @HostListener('window:beforeunload', ['$event'])
  /**
   * Browser beforeunload handler.
   *
   * Shows a confirmation dialog when the user attempts to close, refresh,
   * or navigate away from the page while there are uploaded files that would be deleted.
   * Prevents accidental loss of uploaded files when an order has not been created
   * and the user is not intentionally navigating back to the upload page.
   * 
   *  @param event The BeforeUnloadEvent provided by the browser
   */

  // eslint-disable-next-line jsdoc/require-jsdoc
  beforeUnloadHandler(event: BeforeUnloadEvent): void {
    if (this.fileList.length > 0 && !this.isOrderCreated && !this.goingToBackPage) {
      event.preventDefault();
      event.returnValue = 'アップロードされたファイルは削除されます。本当にこのページを離れますか？';

    }
  }


  /**
   * Handle navigation inside the app (e.g. router navigation away from this component).
   *
   * If there are uploaded files and no order has been created, this should trigger
   * the cleanup/confirmation flow so files are not left orphaned on the server.
   */
  handlePageLeave() {
    if (this.fileList.length > 0 && !this.isOrderCreated && !this.goingToBackPage) {
      const confirmLeave: boolean = confirm('アップロードされたファイルは削除されます。このページを離れてもよろしいですか？');
      if (!confirmLeave) {
        this.router.navigateByUrl(this.router.url); // Stay on page
        return;
      }

      this.deleteAllUploadedFiles();
    }
  }

  /**
   * Delete all uploaded files from the server.
   *
   * Filters and maps this.fileList to the expected request shape and calls the
   * decomUploadService to delete files. Errors are logged and handled via subscription.
   */
  deleteAllUploadedFiles() {
    if (this.fileList.length === 0) return;

    const filesToDelete: FileInfo[] = this.fileList.map(file => ({
      file_id: file.file_id,
      file_path: file.delete_path!
    }));

    const deleteRequest: AttachmentDeleteRequest = {
      order_record_id: undefined, // No order yet
      files: filesToDelete
    };

    this.decomUploadService.deleteUploadFile(deleteRequest).subscribe({
      next: (res) => {
        console.log('All uploaded files deleted on page leave:', res);
      },
      error: (err) => {
        console.error('Failed to delete files on page leave:', err);
      }
    });
  }
  /**
   *Angular lifecycle hook.
   */
  ngOnDestroy() {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }
  /**
   * Angular lifecycle hook.
   * Fetches the initial customer list user id wise on component initialization.
   */
  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.order_id = params['order_id']
    });
    this.getOrderDetails()

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const stateData: any = history.state;
    this.customerData = stateData.customerData;
    // this.currentDate.length
    this.fileList = stateData.fileList;
    this.hospitalName = stateData.hospitalName;
    this.selectedProfileId = stateData.profile_id;
    this.selectedUserName = stateData.selectedUserName;


  }

  /**
   * Get a display name for the customer.
   *
   * Preference order:
   * 1. Use orderDetails[0].customer_name if present.
   * 2. Otherwise build from customerData.first_name and customerData.last_name,
   *    substituting '無し' when a name part is missing. Append serial_no in parentheses
   *    when available.
   * 3. Return '無し' when no name information exists.
   *
   * @returns The computed customer display name.
   */
  getCustomerDisplayName(): string {
    if (this.orderDetails?.[0]?.customer_name) {
      return `${this.orderDetails[0].customer_name} (${this.orderDetails[0].controll_no})`
    } else if (this.customerData?.first_name || this.customerData?.last_name) {
      const name: string = `${this.customerData.first_name ?? '無し'} ${this.customerData.last_name ?? '無し'}`.trim();
      return this.customerData.serial_no ? `${name} (${this.customerData.serial_no})` : name;
    } else {
      return '無し';
    }
  }
  /**
   * Angular lifecycle hook.
   * Fetches the initial customer list user id wise on component initialization.
   */
  getOrderDetails() {
    if (this.order_id) {
      this.orderService.getOrderDetails(this.order_id).subscribe({
        next: (response) => {

          // ✅ You can store response in a variable and show it in the UI
          this.orderDetails = response.order_record
          this.orderFiles = this.orderDetails[0].order_files

        },
        error: (error) => {
          console.error("API Error:", error);
        }
      });
    }
  }

  /**
   * Navigates back to the previous page.
   */
  back() {

    if (this.customerData) {
      this.goingToBackPage = true; // Set flag to true when navigating back
      this.router.navigate(['dashboard/x-ray-new'], {
        queryParams: { user_id: this.customerData.user_id },
        state: {
          customerData: this.customerData,
          fileList: this.fileList,
          hospitalName: this.hospitalName,
          profile_id: this.selectedProfileId,
          selectedUserName: this.selectedUserName
        }
      });
    } else {
      this.location.back();
    }

  }

  /**
   * Combined file list accessor.
   * Returns a merged array containing existing order files (orderFiles)
   * followed by newly uploaded/preview files (fileList). Both sources may be
   * undefined or empty; this getter normalizes them to arrays so the template
   * or callers can iterate safely.
   *
   * @returns An array of files (order files first, then uploaded files)
   */
  get allFiles(): OrderFilesResponseInfo[] {
    return [...this.orderFiles || [], ...this.fileList || []];
  }

  /**
   * Generate a random alphanumeric identifier.
   *
   * Default length is 24 characters. Characters used: A-Z, a-z, 0-9.
   *
   * @param length Number of characters to generate (default: 24)
   * @returns A random string of the requested length
   */
  private generateRandomId(length: number = 24): string {
    const chars: string = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const randomPart: string = Array.from({ length }, () =>
      chars.charAt(Math.floor(Math.random() * chars.length))
    ).join(String()); // avoid ''

    const timestamp: string = Date.now().toString(); // current milliseconds
    return randomPart + timestamp; // append timestamp
  }

  /**
   * Creates a new order using selected profile, customer info and uploaded file ids.
   * Prevents duplicate submissions by checking the loading flag.
   * On success navigates to the x-ray list view.
   */
  orderCreate() {
    this.isOrderCreated = true;
    // Prevent multiple submissions
    if (this.loading) {
      console.warn("Order creation is already in progress.");
      return;
    }
    const fileIds: FileIdsInfo[] = this.fileList.map(file => ({
      file_id: file.file_id
    }));

    const inputDate: OrderRecordCreateRequest = {
      profile_id: this.selectedProfileId,
      hospital_name: this.hospitalName?.trim() || null,
      customer_name: `${this.customerData.first_name} ${this.customerData.last_name}`,
      phone_number: this.customerData.phone_number,
      email_address: this.customerData.email_address,
      application_number: this.generateRandomId(24), // Generate random 24-char ID
      file_ids: fileIds
    }

    this.loading = true; // Disable further actions
    this.orderService.orderCreate(inputDate).subscribe({
      next: (response) => {
        console.log('order create success:', response);
        this.loading = false; // Reset loading state
        // ✅ Navigate to x-ray list after success
        this.menuService.setMenu('x-ray-list');

        this.router.navigate(['/dashboard/x-ray-list']);

      },
      error: (error) => {
        console.error('Failed to delete file:', error);
        this.loading = false; // Reset loading even on error
      }
    });

  }


  /**
   * The currently selected image URL for the preview popup/modal.
   * When non-null the template should display an image preview using this URL.
   */
  selectedImageUrl: string | null = null;

  /**
   * Open the image preview popup.
   *
   * Sets `selectedImageUrl` to the provided image URL so the template
   * can display a modal/overlay with the image.
   *
   * @param imageUrl - Absolute or relative URL of the image to preview.
   */
  openImagePopup(imageUrl: string): void {
    this.selectedImageUrl = imageUrl;
  }

  /**
   * Close the image preview popup.
   *
   * Clears `selectedImageUrl`, which should cause the template to hide
   * the image modal/overlay.
   */
  closeImagePopup(): void {
    this.selectedImageUrl = null;
  }


}
